import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Play, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { useToast } from 'components/ui/use-toast';
import { QueryClientContext } from '@tanstack/react-query';
import { useAuthContext } from 'modules/auth';
import { UpdateProfile } from 'modules/auth/api';
import { PERMISSIONS } from 'modules/auth/permissions';
import { ClearDemoWorkspace } from './api';

export interface DemoWorkspaceBannerProps {
  onStartTour: () => void;
}

export const DemoWorkspaceBanner: React.FC<DemoWorkspaceBannerProps> = ({ onStartTour }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = React.useContext(QueryClientContext);
  const { state, methods } = useAuthContext();
  const orgId = state.organization?.id;
  // Clearing purges org-wide sample data; the API requires TESTS_DELETE.
  const canClear = Boolean(state.permissions?.includes(PERMISSIONS.TESTS_DELETE));

  const [isDismissed, setIsDismissed] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasDemoWorkspace = Boolean((state.organization as any)?.hasDemoWorkspace);
  const isTourDismissed = Boolean(state.user?.tourDismissed);

  if (!hasDemoWorkspace || isDismissed || isTourDismissed || !orgId) {
    return null;
  }

  const handleDismiss = async () => {
    setIsDismissed(true);
    const storageKey = orgId ? `examing_tour_completed_${orgId}` : 'examing_tour_completed';
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {}

    if (state.user && !state.user.tourDismissed) {
      try {
        await UpdateProfile({ tourDismissed: true });
        if (methods?.setUser) {
          methods.setUser((prev) => (prev ? { ...prev, tourDismissed: true } : prev));
        }
        queryClient?.invalidateQueries({ queryKey: ['auth', 'profile'] });
      } catch {
        // non-blocking
      }
    }
  };

  const handleClearDemo = async () => {
    try {
      setIsDeleting(true);
      await ClearDemoWorkspace(orgId);
      toast.success(t('tour.demo_cleared', 'Namuna ma‘lumotlar muvaffaqiyatli tozalandi'));
      setIsConfirmOpen(false);
      // Refresh organization/profile state and invalidate data
      if (methods?.setOrganization && state.organization) {
        methods.setOrganization({
          ...state.organization,
          hasDemoWorkspace: false,
        });
      }
      queryClient?.invalidateQueries();
    } catch {
      toast.error(t('error_general', 'Xatolik yuz berdi. Qayta urinib ko‘ring.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        id="demo-workspace-banner"
        className="relative overflow-hidden rounded-xl border border-primary/30 bg-primary/5 p-4 sm:p-5 dark:border-primary/20 dark:bg-primary/10 transition-all duration-200"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-primary-foreground dark:text-primary">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold tracking-tight text-foreground">
                  {t('tour.banner_title', 'Namuna ish maydoni faol')}
                </h3>
                <Badge variant="outline" className="border-primary/40 bg-background/50 text-[11px] font-medium text-foreground">
                  {t('tour.badge_sample', 'Namuna')}
                </Badge>
              </div>
              <p className="max-w-2xl text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {t(
                  'tour.banner_desc',
                  'Siz tanlagan yo‘nalish bo‘yicha tayyor fan, savollar banki va to‘liq sozlangan test yaratildi. Tizim imkoniyatlarini o‘rganish uchun qo‘llanmani boshlang yoki xohlagan vaqtda tozalang.',
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <Button
              size="sm"
              onClick={onStartTour}
              className="bg-primary font-semibold text-primary-foreground hover:opacity-90 shadow-sm"
            >
              <Play className="mr-1.5 h-3.5 w-3.5 fill-current" />
              {t('tour.start_tour', 'Qo‘llanmani boshlash')}
            </Button>

            {canClear && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsConfirmOpen(true)}
              className="border-border text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              {t('tour.clear_sample_data', 'Tozalash')}
            </Button>
            )}

            <button
              type="button"
              onClick={handleDismiss}
              aria-label={t('close', 'Yopish')}
              className="rounded-md p-1.5 text-muted-foreground/70 hover:bg-muted hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog before purging demo workspace */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('tour.confirm_clear_title', 'Namuna ma‘lumotlarni o‘chirish')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'tour.confirm_clear_desc',
                'Haqiqatan ham barcha namuna test, savollar va guruhlarni tozalamoqchimisiz? Siz o‘zingiz yaratgan ma‘lumotlar to‘liq saqlanib qoladi.',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('cancel', 'Bekor qilish')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearDemo}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('deleting', 'O‘chirilmoqda...')}
                </>
              ) : (
                t('tour.confirm_delete_btn', 'Ha, barchasini o‘chirish')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DemoWorkspaceBanner;
